/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Loader2, ClipboardList } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import reviewConstants from '../../../constants/review.constants';
import AppLayout from '../../../layouts/AppLayout';
import ApiService from '../../../services/api.service';
import ReviewHistoryTable from './components/ReviewHistoryTable';

function Reviews({ title }) {
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [reviewHistoryData, setReviewHistoryData] = useState([]);
  const [sortField, setSortField] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchReviews = async (skip, limit, sort) => {
    try {
      const response = await ApiService.developer.getReviewsHistory({
        params: {
          skip: skip * limit,
          limit,
          sort,
        },
      });

      setCurrentPage(skip);
      setReviewHistoryData(response.paginatedResult);
      setTotalCount(response.totalCount ?? 0);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const sortParam = sortOrder === 'asc' ? sortField : `-${sortField}`;
    fetchReviews(currentPage, rowsPerPage, sortParam);
  }, [currentPage, rowsPerPage, sortField, sortOrder]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setIsLoading(true);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setIsLoading(true);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(0);
    setIsLoading(true);
  };

  if (isLoading && reviewHistoryData.length === 0) {
    return (
      <AppLayout title={title}>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={title}>
      <div className="h-full overflow-auto">
        <div className="max-w-6xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-primary" />
                Review History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reviewHistoryData.length === 0 && !isLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <ClipboardList className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">
                    {reviewConstants.ERROR_MESSAGES.REVIEWS_ARE_NOT_FOUND}
                  </p>
                </div>
              ) : (
                <ReviewHistoryTable
                  data={reviewHistoryData}
                  isLoading={isLoading}
                  totalCount={totalCount}
                  currentPage={currentPage}
                  rowsPerPage={rowsPerPage}
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}

Reviews.propTypes = {
  title: PropTypes.string.isRequired,
};

export default Reviews;
